/**
 * Pure reducer for the MOPS toast queue. Split out from the React provider so
 * the queue semantics (newest-on-top, max-visible cap, dismiss-by-id) can be
 * exercised in vitest's `node` environment without DOM bootstrapping.
 */

export type ToastType = 'success' | 'error' | 'info';

export interface IToast {
  id: string;
  type: ToastType;
  message: string;
}

export interface IToastState {
  toasts: IToast[];
}

export type ToastAction = { type: 'show'; toast: IToast } | { type: 'dismiss'; id: string };

export const MAX_VISIBLE_TOASTS = 5;

export const initialToastState: IToastState = { toasts: [] };

export const toastReducer = (state: IToastState, action: ToastAction): IToastState => {
  switch (action.type) {
    case 'show': {
      // Newest-on-top. When the cap is exceeded the oldest (tail) is dropped —
      // the operator sees the freshest state. Errors do not survive longer than
      // success/info; the cap is a hard ceiling, not a priority queue.
      const next = [action.toast, ...state.toasts.filter((t) => t.id !== action.toast.id)];
      return { toasts: next.slice(0, MAX_VISIBLE_TOASTS) };
    }
    case 'dismiss':
      return { toasts: state.toasts.filter((t) => t.id !== action.id) };
    default:
      return state;
  }
};
